import { drawingPreviewPoints } from '../shared/drawing-preview.js';
import type { LegendSymbol } from '../shared/drawing-legend.js';
import { dataIconSchema } from '../shared/data-icon.js';
import { isMarkerIconName } from '../shared/marker-icon.js';
import { getStrokeOpacity, getPolygonFillOpacity, getPolygonOutlineOpacity } from './stroke-style.js';
import { landmarkMarker, landmarkSvgSource } from './landmark-markers.js';
import { markerIconSvg } from './marker-icons.js';

function color(value: string | undefined): string { return value && /^#[0-9a-f]{6}$/i.test(value) ? value : '#5b4bff'; }

/** Legend colors use equal-sized squares; picker previews keep drawing geometry. */
export function drawingLegendSwatch(stroke: LegendSymbol): string {
  if (stroke.tool === 'marker' || stroke.dataPoint) return drawingLegendSymbol(stroke);
  const ink = color(stroke.tool === 'polygon' ? stroke.fillColor ?? stroke.color : stroke.color);
  const opacity = stroke.tool === 'polygon' ? getPolygonFillOpacity(stroke) : getStrokeOpacity(stroke);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24" aria-hidden="true"><rect x="8" y="2" width="20" height="20" fill="${ink}" opacity="${opacity}"/></svg>`;
}

/** Only validated colors and our own marker SVG enter this markup. Labels remain text. */
export function drawingLegendSymbol(stroke: LegendSymbol): string {
  const ink = color(stroke.color);
  if (stroke.layerSwatch) return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24"><rect x="7" y="3" width="22" height="18" rx="3" fill="${ink}" opacity="${getStrokeOpacity(stroke)}"/></svg>`;
  if (stroke.dataPoint) {
    const icon = dataIconSchema.safeParse(stroke.dataPoint);
    const pointSymbol = icon.success ? `<image href="${icon.data.src}" x="7" y="1" width="22" height="22" opacity="${icon.data.opacity}"/>`
      : `<circle cx="18" cy="12" r="7" fill="${ink}" stroke="white" stroke-width="2"/>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24" opacity="${getStrokeOpacity(stroke)}">${pointSymbol}</svg>`;
  }
  if (stroke.tool === 'marker') {
    const name = isMarkerIconName(stroke.markerIcon) ? stroke.markerIcon : 'map-pin';
    const icon = landmarkMarker(name) ? `<image href="${landmarkSvgSource(name)}" width="22" height="22"/>` : markerIconSvg(name);
    return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24" color="${ink}" opacity="${getStrokeOpacity(stroke)}"><g transform="translate(7 1)">${icon}</g></svg>`;
  }
  const width = Math.max(1, Math.min(5, stroke.width));
  const dashed = stroke.lineStyle === 'dashed' ? ' stroke-dasharray="5 4"' : '';
  const outline = `stroke="${ink}" stroke-width="${width}" stroke-opacity="${stroke.tool === 'polygon' ? getPolygonOutlineOpacity(stroke) : getStrokeOpacity(stroke)}"${dashed}`;
  const path = drawingPathPreview(stroke, outline);
  const symbol = path ?? (stroke.tool === 'polygon'
    ? `<path d="M5 19V8L15 3L30 7V19Z" fill="${color(stroke.fillColor ?? stroke.color)}" fill-opacity="${getPolygonFillOpacity(stroke)}" ${outline}/>`
    : stroke.tool === 'circle' ? `<circle cx="18" cy="12" r="9" fill="none" ${outline}/>`
      : `<path d="M3 12H32${stroke.tool === 'arrow' ? 'M25 5L32 12L25 19' : ''}" fill="none" ${outline}/>`);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24" aria-hidden="true">${symbol}</svg>`;
}

function drawingPathPreview(stroke: LegendSymbol, outline: string): string | null {
  if (!['line', 'arrow', 'freehand', 'polygon'].includes(stroke.tool)) return null;
  const points = drawingPreviewPoints(stroke);
  if (!points) return null;
  const path = stroke.tool === 'polygon'
    ? `<polygon points="${points}" fill="${color(stroke.fillColor ?? stroke.color)}" fill-opacity="${getPolygonFillOpacity(stroke)}" ${outline}/>`
    : `<polyline points="${points}" fill="none" ${outline}/>`;
  return `<g transform="translate(2 2)" stroke-linecap="round" stroke-linejoin="round">${path}${stroke.tool === 'arrow' ? previewArrowhead(points, outline) : ''}</g>`;
}

function previewArrowhead(points: string, outline: string): string {
  const coordinates = points.split(' ').map(point => point.split(',').map(Number));
  const tip = coordinates.at(-1)!;
  const previous = coordinates.slice(0, -1).reverse().find(point => point[0] !== tip[0] || point[1] !== tip[1]);
  if (!previous) return '';
  const angle = Math.atan2(tip[1] - previous[1], tip[0] - previous[0]);
  const wing = (offset: number) => `${(tip[0] - 5 * Math.cos(angle + offset)).toFixed(1)},${(tip[1] - 5 * Math.sin(angle + offset)).toFixed(1)}`;
  return `<polyline points="${wing(.6)} ${tip.join(',')} ${wing(-.6)}" fill="none" ${outline}/>`;
}
